import React, { useEffect, useState, useContext } from 'react'
import { StyledGrid, Label } from '../../style'
import Block from './Block'
import clickHandlerService from '../../click-handler-service'
import { DashboardContext } from 'live/context/dashboard'
import { cloneDeepGridGroup } from '../../cloneDeepFunctions'
import TableGridComponent from '../../components/TableGridComponent'
import { useChannelsConfigWithStandardChannelsInfo } from '../../remote'

export const Grid: React.FC<GridProps> = ({ state, widget }: GridProps) => {
    const cfg = widget?.jsonConfig ?? {}
    const groups = GroupsWithEvents(cfg.kind, cfg.groups, state)
    const colors = cfg.kind === 'GridPipeless' ? cfg.groups?.map(g => g.groupColor) : cfg.properties?.map(p => p.color)
    const dark = useContext(DashboardContext)?.theme === 'dark'

    return <GridComponent groups={groups} colors={colors} dark={dark} tableModeConfig={cfg?.tableModeConfig} />
}

export const GridWithAlarms: React.FC<GridWithAlarmsProps> = ({
    state,
    widget,
    useSoundAlarms,
    useAlarmRules,
    useAssetAlarms,
    openSideview
}: GridWithAlarmsProps) => {
    const dashContext = useContext(DashboardContext)
    const dark = dashContext?.theme === 'dark'
    const assetName = dashContext?.state?.filterValues?.asset_name_event_type?.[0]

    const widgetId = widget?.id
    const cfg = widget?.jsonConfig ?? {}
    const colors = cfg.kind === 'GridPipeless' ? cfg.groups?.map(g => g.groupColor) : cfg.properties?.map(p => p.color)

    const groups = GroupsWithEvents(cfg.kind, cfg.groups, state) ?? []
    const [updatedGroups, setUpdatedGroups] = useState(groups)

    const channels = groups.reduce(
        (channels, group) => new Set([...channels, ...group.items?.map(item => item.mnemonic)]),
        new Set<string>()
    )
    const rules = useAlarmRules()
    const alarms = useAssetAlarms()
    useSoundAlarms(widgetId, [...channels])

    useEffect(() => {
        const newgroups = GroupsWithEvents(cfg.kind, cfg.groups, state) ?? []

        !assetName && widgetId !== 'testId'
            ? setUpdatedGroups(newgroups)
            : setUpdatedGroups(
                  newgroups.map(group => ({
                      ...group,
                      items: group.items?.map(item => {
                          let changes = {}

                          const alarmsForItem = alarms.filter(
                              alarm =>
                                  alarm.ref.mnemonic && alarm.ref.mnemonic === item.mnemonic && alarm.status === 'open'
                          )

                          if (alarmsForItem.length) {
                              const highestSeverity = alarmsForItem.reduce(
                                  (highest, alarm) => (highest !== 'critical' ? alarm.severity : highest),
                                  ''
                              )

                              if (highestSeverity === 'critical' || highestSeverity === 'warning') {
                                  if (item.notMapped) {
                                      changes = {
                                          backgroundColor: item?.alarms?.notMapped?.background || 'blue',
                                          iconColor: item?.alarms?.notMapped?.iconColor || 'blue',
                                          __icon: item?.alarms?.notMapped?.icon || 'times',
                                          __blink: item?.alarms?.notMapped?.blink || 1000
                                      }
                                  } else {
                                      changes = {
                                          backgroundColor:
                                              item?.alarms?.[highestSeverity]?.background ||
                                              (highestSeverity === 'critical' ? '#cc2900' : '#e6e600'),
                                          iconColor:
                                              item?.alarms?.[highestSeverity]?.iconColor ||
                                              (highestSeverity === 'critical' ? '#cc2900' : '#e6e600'),
                                          __icon: item?.alarms?.[highestSeverity]?.icon || 'exclamation',
                                          __blink: item?.alarms?.[highestSeverity]?.blink || 1000
                                      }
                                  }
                              } else {
                                  console.warn('Unexpected severity for alarms:', alarmsForItem)
                              }
                          } else if (
                              !item.notMapped &&
                              [...rules.userRules, ...rules.globalRules].some(rule => rule.channel === item.mnemonic)
                          ) {
                              changes = {
                                  backgroundColor: item?.alarms?.configured?.background,
                                  iconColor: item?.alarms?.configured?.iconColor,
                                  defaultIconColor: 'green',
                                  __icon: item?.alarms?.configured?.icon || 'check'
                              }
                          } else if (item.notMapped) {
                              changes = {
                                  backgroundColor: item?.alarms?.notMapped?.background || 'blue',
                                  iconColor: item?.alarms?.notMapped?.iconColor || 'blue',
                                  __icon: item?.alarms?.notMapped?.icon || 'times'
                              }
                          }

                          return {
                              ...item,
                              ...changes
                          }
                      })
                  }))
              )
    }, [assetName, rules, alarms, state, cfg.kind, cfg.groups, widgetId])

    return (
        <GridComponent
            groups={updatedGroups}
            colors={colors}
            dark={dark}
            clickHandler={openSideview}
            tableModeConfig={cfg?.tableModeConfig}
        />
    )
}

const GridComponent: React.FC<GridComponentProps> = ({
    groups,
    colors,
    dark,
    clickHandler,
    tableModeConfig
}: GridComponentProps) => {
    
    if (tableModeConfig?.enable) {
        const channelsConfig = groups.flatMap(group =>
            group.items.map(item => ({
                ...item,
                groupTitle: group.title, // not used yet
                nColumns: group.nColumns, // not used yet
                titleFontSize: group.titleFontSize,
                valueFontSize: group.valueFontSize,
                uomFontSize: group.uomFontSize,
                titleBold: group.titleBold,
                valueBold: group.valueBold,
                uomBold: group.uomBold
            }))
        )
        const channelsWithStdInfo = useChannelsConfigWithStandardChannelsInfo(channelsConfig)
        return <TableGridComponent columns={tableModeConfig.columns} rows={tableModeConfig.rows} data={channelsWithStdInfo} />
    }

    return (
        <StyledGrid dark={dark}>
            <div className="grid-widget-container">
                {groups?.map((group, i) => (
                    <React.Fragment key={i}>
                        {group.title && (
                            <Label
                                className="groupTitle"
                                titleFontSize={group.groupTitleFontSize ?? group.titleFontSize}
                                titleBold={group.titleBold}
                            >
                                {group.title}
                            </Label>
                        )}
                        <div className="dataLayer" key={i}>
                            {group.items?.map((item, j) => {
                                const _background = item.backgroundColor || item.__color
                                const backgroundColor = _background?.endsWith(', 0)') ? undefined : _background

                                return (
                                    <Block
                                        key={j}
                                        mnemonic={item.mnemonic}
                                        lastTimestamp={item.lastTimestamp}
                                        title={item.title}
                                        __blink={item.__blink}
                                        value={item.value}
                                        __html={item.__html}
                                        __titleHtml={item.__titleHtml}
                                        __icon={item.__icon}
                                        __uom={item.__uom}
                                        iconColor={item.iconColor || item.__color}
                                        defaultIconColor={item.defaultIconColor}
                                        backgroundColor={backgroundColor}
                                        color={colors && colors[i]}
                                        nColumns={group.nColumns}
                                        uomFontSize={group.uomFontSize}
                                        valueFontSize={group.valueFontSize}
                                        titleFontSize={group.titleFontSize}
                                        titleBold={group.titleBold}
                                        valueBold={group.valueBold}
                                        uomBold={group.uomBold}
                                        clickHandler={
                                            typeof clickHandler === 'function'
                                                ? clickHandler
                                                : clickHandlerService.getClickHandler()
                                        }
                                    />
                                )
                            })}
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </StyledGrid>
    )
}

const GroupsWithEvents = (kind, cfgGroups, state): GridGroup[] => {
    const groups: GridGroup[] = getGroupsWithInitialConfigs(kind, cfgGroups, state)

    return getGroupsWithEvents(kind, cfgGroups, groups, state)
}

const getGroupsWithInitialConfigs = (
    kind: string,
    cfgGroups: GridEditorGroup[] = [],
    state: GridReducerState
): GridGroup[] => {
    const groups = []

    if (kind === 'GridPipeless') {
        cfgGroups.forEach((group, i) => {
            const g = groups[i] ?? {}
            g.items = []
            group.channels?.forEach((channel, j) => {
                g.items[j] = newEventObject(channel)
            })
            if (g.title !== group?.title) g.title = group?.title
            if (g.nColumns !== group?.nColumns) g.nColumns = group?.nColumns
            if (g.groupTitleFontSize !== group?.groupTitleFontSize) g.groupTitleFontSize = group?.groupTitleFontSize
            if (g.titleFontSize !== group?.titleFontSize) g.titleFontSize = group?.titleFontSize
            if (g.valueFontSize !== group?.valueFontSize) g.valueFontSize = group?.valueFontSize
            if (g.uomFontSize !== group?.uomFontSize) g.uomFontSize = group?.uomFontSize
            if (g.titleBold !== group?.titleBold) g.titleBold = group?.titleBold
            if (g.valueBold !== group?.valueBold) g.valueBold = group?.valueBold
            if (g.uomBold !== group?.uomBold) g.uomBold = group?.uomBold
            groups[i] = g
        })

        return groups
    }

    state?.forEach((events, idx) => {
        const info = events?.find(e => e?.info !== undefined)?.info
        groups[idx] = {
            items: events?.filter(e => e && e.info === undefined) || [],
            title: info?.groupTitle || '',
            nColumns: info?.nColumns || 3,
            groupTitleFontSize: info?.groupTitleFontSize || 14,
            titleFontSize: info?.titleFontSize || 14,
            valueFontSize: info?.valueFontSize || 20,
            uomFontSize: info?.uomFontSize || 16,
            titleBold: info?.titleBold || true,
            valueBold: info?.valueBold || false,
            uomBold: info?.uomBold || false
        }
    })

    return groups
}

const getGroupsWithEvents = (
    kind: string,
    cfgGroups: GridEditorGroup[] = [],
    groups: GridGroup[],
    state: GridReducerState
): GridGroup[] => {
    const newGroups: GridGroup[] =
        groups?.map(group => {
            return cloneDeepGridGroup(group)
        }) ?? []

    if (kind === 'GridPipeless') {
        cfgGroups.forEach((g, i) => {
            g?.channels?.forEach((channel, j) => {
                state?.forEach(layerEvents => {
                    const event = layerEvents[1] ?? {}
                    if (!newGroups[i].items[j]) newGroups[i].items[j] = newEventObject(channel)
                    if (channel?.mnemonic === event.mnemonic) {
                        newGroups[i].items[j].lastTimestamp = event.timestamp
                        newGroups[i].items[j].title = event.title
                        newGroups[i].items[j].value = event.value
                        newGroups[i].items[j].__uom = event.uom
                    }
                    newGroups[i].items[j].notMapped =
                        !channel?.expression && isNotMapped(channel?.mnemonic, layerEvents[0]?.info?.curves)
                })
            })
        })

        return newGroups
    }

    return newGroups?.map((group, idx) => ({
        ...group,
        items: state[idx]?.filter(e => e && e.info === undefined) ?? []
    }))
}

const newEventObject = (channel: GridEditorChannelOption = {}): GridEvent => {
    return {
        mnemonic: channel.mnemonic,
        title: channel.title || channel.label,
        __icon: channel.__icon,
        iconColor: channel.iconColor,
        defaultIconColor: '',
        backgroundColor: channel.backgroundColor,
        alarms: {
            configured: {
                background: channel.alarms?.configured?.background,
                iconColor: channel.alarms?.configured?.iconColor,
                icon: channel.alarms?.configured?.icon
            },
            warning: {
                background: channel.alarms?.warning?.background,
                iconColor: channel.alarms?.warning?.iconColor,
                icon: channel.alarms?.warning?.icon,
                blink: channel.alarms?.warning?.blink
            },
            critical: {
                background: channel.alarms?.critical?.background,
                iconColor: channel.alarms?.critical?.iconColor,
                icon: channel.alarms?.critical?.icon,
                blink: channel.alarms?.critical?.blink
            },
            notMapped: {
                background: channel.alarms?.notMapped?.background,
                iconColor: channel.alarms?.notMapped?.iconColor,
                icon: channel.alarms?.notMapped?.icon
            }
        }
    }
}

const isNotMapped = (mnemonic: string, curves?: string): boolean => {
    return curves ? !curves?.[mnemonic]?.options?.length : false
}
